import { Location } from "@angular/common";
import { Component, Input, OnChanges, OnInit, SimpleChanges, ViewEncapsulation } from "@angular/core";
import { ActivatedRoute } from "@angular/router";
import { AgPieSeriesTooltipRendererParams } from "ag-charts-community";
import { ColDef } from "@ag-grid-community/core";
import { AccountFulfillmentTime } from "app/model/entities/-chart-classes";

import { DbQueryService } from "app/shared/db-query.service";
import { DomainBaseComponent } from "app/shared/domain-base.component";
import { DomainService } from "app/shared/domain.service";
import _ from "lodash";
import * as ExcelJS from "exceljs";
import { saveAs } from "file-saver";

const MONTHS_BACK = 6;
const TOP_ACCOUNT_COUNT = 10;
const ALL_OTHERS_LABEL = "All others";

/** One pie slice: an account, or the rolled-up balance of every account past the top 10. */
interface AccountSlice {
  accountId: number;
  accountName: string;
  itemCount: number;
  avgDays: number;
}

/** A detail row with the average derived from the server's ItemCount + TotalDays. */
interface FulfillmentRow extends AccountFulfillmentTime {
  avgDays: number;
}

@Component({
  selector: "app-chart-account-fulfillment-time",
  templateUrl: "./chart-account-fulfillment-time.component.html",
  encapsulation: ViewEncapsulation.None,
  standalone: false,
})
export class ChartAccountFulfillmentTimeComponent extends DomainBaseComponent implements OnInit, OnChanges {
  /** Accounts set to Exclude from Dashboard Analytics that this view puts back in. */
  @Input() includedAccountIds: number[] = [];
  options: any;
  private detailRows: FulfillmentRow[] = [];
  private sliceData: AccountSlice[] = [];

  constructor(
    protected domainService: DomainService,
    protected route: ActivatedRoute,
    protected location: Location,
    protected dbQueryService: DbQueryService
  ) {
    super(domainService);
  }

  async ngOnInit() {
    await this.processChartData();
    this.isPageReady = true;
  }

  async ngOnChanges(changes: SimpleChanges) {
    if (changes["includedAccountIds"] && !changes["includedAccountIds"].firstChange) {
      await this.processChartData();
    }
  }

  private async processChartData() {
    const data = await this.dbQueryService.getAccountFulfillmentTimes(MONTHS_BACK, this.includedAccountIds);

    this.detailRows = _.orderBy(
      (data ?? []).map((d) => ({ ...d, avgDays: this.avg(d.totalDays, d.itemCount) })),
      ["accountName", "manufacturerName", "style"]
    );

    this.sliceData = this.buildSlices(data ?? []);

    if (this.sliceData.length === 0) {
      this.options = null;
      return;
    }

    this.options = {
      title: { text: this.getTitle() },
      data: this.sliceData,
      series: [
        {
          type: "pie",
          angleKey: "itemCount",
          calloutLabelKey: "accountName",
          legendItemKey: "accountName",
          sectorLabelKey: "avgDays",
          sectorLabel: {
            formatter: (params: { value: number }) => `${params.value} d`,
          },
          tooltip: { renderer: this.renderer.bind(this) },
        },
      ],
    };
  }

  /** Top 10 accounts by shipped-item count, plus one slice for the balance.
   *  Averages come from summed days over summed items, never from averaging averages. */
  private buildSlices(data: AccountFulfillmentTime[]): AccountSlice[] {
    const byAccount = _.map(_.groupBy(data, (d) => d.accountId), (rows) => {
      const itemCount = _.sumBy(rows, (r) => r.itemCount);
      const totalDays = _.sumBy(rows, (r) => r.totalDays);
      return {
        accountId: rows[0].accountId,
        accountName: rows[0].accountName,
        itemCount,
        avgDays: this.avg(totalDays, itemCount),
      } as AccountSlice;
    });

    const ranked = _.orderBy(byAccount, (a) => a.itemCount, "desc");
    const top = ranked.slice(0, TOP_ACCOUNT_COUNT);
    const rest = ranked.slice(TOP_ACCOUNT_COUNT);
    if (rest.length === 0) return top;

    const restAccountIds = new Set(rest.map((a) => a.accountId));
    const restRows = data.filter((d) => restAccountIds.has(d.accountId));
    const restItemCount = _.sumBy(restRows, (r) => r.itemCount);

    return top.concat({
      accountId: -1,
      accountName: `${ALL_OTHERS_LABEL} (${rest.length})`,
      itemCount: restItemCount,
      avgDays: this.avg(_.sumBy(restRows, (r) => r.totalDays), restItemCount),
    });
  }

  private avg(totalDays: number, itemCount: number) {
    return itemCount === 0 ? 0 : _.round(totalDays / itemCount, 1);
  }

  getTitle() {
    return `Average Days to Ship by Account - Last ${MONTHS_BACK} Months`;
  }

  renderer(params: AgPieSeriesTooltipRendererParams<AccountSlice>) {
    const d = params.datum;
    return {
      title: d.accountName,
      content: `${d.itemCount} items shipped<br/>${d.avgDays} days average`,
    };
  }

  // ViewableWidget interface methods
  async exportToExcel(): Promise<void> {
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet("Fulfillment Time");
    const exportDate = new Date();

    worksheet.addRow([this.getTitle()]);
    worksheet.getRow(1).font = { bold: true, size: 14 };
    worksheet.mergeCells("A1:F1");

    worksheet.addRow([`Exported: ${exportDate.toLocaleDateString()} ${exportDate.toLocaleTimeString()}`]);
    worksheet.getRow(2).font = { italic: true, size: 10 };
    worksheet.mergeCells("A2:F2");

    worksheet.addRow([]);

    worksheet.addRow(["Account", "Manufacturer", "Style", "Description", "Items Shipped", "Avg Days"]);
    const headerRow = worksheet.getRow(4);
    headerRow.font = { bold: true };
    headerRow.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FFE0E0E0" } };

    worksheet.getColumn(1).width = 32;
    worksheet.getColumn(2).width = 24;
    worksheet.getColumn(3).width = 16;
    worksheet.getColumn(4).width = 40;
    worksheet.getColumn(5).width = 14;
    worksheet.getColumn(6).width = 12;

    this.detailRows.forEach((r) => {
      worksheet.addRow([r.accountName, r.manufacturerName, r.style, r.description, r.itemCount, r.avgDays]);
    });

    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
    saveAs(blob, `Account_Fulfillment_Time_${exportDate.toISOString().split("T")[0]}.xlsx`);
  }

  getGridData(): FulfillmentRow[] {
    return this.detailRows;
  }

  getColumnDefs(): ColDef[] {
    return [
      { field: "accountName", headerName: "Account", flex: 2 },
      { field: "manufacturerName", headerName: "Manufacturer", flex: 1 },
      { field: "style", headerName: "Style", flex: 1 },
      { field: "description", headerName: "Description", flex: 2 },
      { field: "itemCount", headerName: "Items Shipped", flex: 1, type: "leftAligned" },
      { field: "avgDays", headerName: "Avg Days", flex: 1, type: "leftAligned" },
    ];
  }

  getExportFileName(): string {
    return "Account_Fulfillment_Time";
  }

  getDialogTitle(): string {
    return this.getTitle();
  }
}
